import {
  InteractiveContainer,
  InteractiveContainerProps,
} from "@jugl-web/ui-components/cross-platform/InteractiveContainer";
import {
  assignRefs,
  cx,
  useAppVariant,
  useUpdateEffect,
} from "@jugl-web/utils";
import { ReactNode, forwardRef, useCallback, useEffect, useRef } from "react";

type CxArgument = Parameters<typeof cx>[number];

export interface TabProps
  extends Omit<InteractiveContainerProps, "className" | "title"> {
  title: ReactNode;
  isSelected: boolean;
  number?: number;
  shouldScrollIntoViewOnSelect?: boolean;
  shouldScrollIntoViewOnMount?: boolean;
  id?: string;
  htmlTitle?: string;
  className?: string | ((isSelected: boolean) => CxArgument);
  labelClassName?: string;
  count?: number;
  indicatorClassName?: string | ((isSelected: boolean) => CxArgument);
  counterClassName?: string;
}

export const Tab = forwardRef<HTMLDivElement, TabProps>(
  (
    {
      title,
      isSelected,
      shouldScrollIntoViewOnSelect = true,
      shouldScrollIntoViewOnMount = false,
      id,
      htmlTitle,
      className,
      labelClassName,
      indicatorClassName,
      count,
      counterClassName,
      number,
      onClick,
      ...props
    },
    ref
  ) => {
    const { isCx } = useAppVariant();
    const containerRef = useRef<HTMLDivElement | null>(null);

    const scrollIntoView = useCallback(() => {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }, []);

    // Scrolls to the tab when it is selected
    useUpdateEffect(() => {
      if (isSelected && shouldScrollIntoViewOnSelect) {
        scrollIntoView();
      }
    }, [isSelected, shouldScrollIntoViewOnSelect, scrollIntoView]);

    // Scrolls to the tab when it is mounted
    useEffect(() => {
      if (shouldScrollIntoViewOnMount) {
        scrollIntoView();
      }
    }, [shouldScrollIntoViewOnMount, scrollIntoView]);

    const textSelectedColor = isCx ? "text-dark-1000" : "text-primary-800";
    const selectedUnderlineBackground = isCx ? "bg-dark" : "bg-primary";

    return (
      <InteractiveContainer
        ref={assignRefs([ref, containerRef])}
        id={id}
        title={htmlTitle || (typeof title === "string" ? title : undefined)}
        className={cx(
          "group flex w-full flex-col justify-between gap-3",
          isSelected ? `${textSelectedColor} font-medium` : "text-dark-700",
          typeof className === "function" ? className(isSelected) : className
        )}
        onClick={onClick}
        {...props}
      >
        <div className="flex items-center justify-center gap-1 px-4 pt-2">
          {typeof number === "number" && (
            <div
              className={cx(
                "mr-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full font-secondary text-[10px] transition-colors",
                isSelected
                  ? "bg-[#2F80ED] font-semibold text-white"
                  : "bg-white font-medium text-[#5A6367]"
              )}
            >
              {number}
            </div>
          )}
          <span
            className={cx(
              "font-secondary text-base whitespace-nowrap",
              labelClassName
            )}
          >
            {title}
          </span>
          {typeof count === "number" && (
            <span
              className={cx(
                "tab-counter h-[19px] shrink-0 rounded-full bg-grey-200 px-2 font-secondary text-xs leading-[160%] font-normal text-dark-700",
                counterClassName
              )}
            >
              {count}
            </span>
          )}
        </div>
        <div
          className={cx(
            "h-1 w-full rounded-lg transition-colors",
            isSelected
              ? `${selectedUnderlineBackground}`
              : "bg-transparent group-hover:bg-grey-300",
            typeof indicatorClassName === "function"
              ? indicatorClassName(isSelected)
              : indicatorClassName
          )}
        />
      </InteractiveContainer>
    );
  }
);
